import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { countFieldsForActor } from './field-configurator.utils';
import { FieldConfiguratorCanvasComponent } from './field-configurator-canvas.component';
import { FieldPreviewPanelComponent } from './field-preview-panel.component';
import { FieldSettingsPanelComponent } from './field-settings-panel.component';
import type {
  FieldConfiguratorActor,
  FieldConfiguratorField,
  FieldConfiguratorMode,
  FieldConfiguratorOptionSets,
  FieldConfiguratorPreviewFilter,
  FieldConfiguratorPreviewMeta,
  FieldConfiguratorSettingAction,
  FieldConfiguratorSettingsPanelOptions,
  FieldPaletteItem,
} from './field-configurator.models';

@Component({
  selector: 'app-field-configurator-workspace',
  imports: [CommonModule, FormsModule, FieldConfiguratorCanvasComponent, FieldSettingsPanelComponent, FieldPreviewPanelComponent],
  templateUrl: './field-configurator-workspace.component.html',
  styleUrl: './field-configurator-workspace.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class FieldConfiguratorWorkspaceComponent {
  @Input() mode: FieldConfiguratorMode = 'configure';
  @Input() actors: FieldConfiguratorActor[] = [];
  @Input() activeActorId = '';
  @Input() actorSelectLabel = 'Recipient';
  @Input() fieldPalette: FieldPaletteItem[] = [];
  @Input() canPlaceFields = false;
  @Input() stepTwoError = '';
  @Input() showWalkthroughButton = false;
  @Input() settingsTitle = 'Field settings';
  @Input() selectedField: FieldConfiguratorField | null = null;
  @Input() settingsPanelOptions!: FieldConfiguratorSettingsPanelOptions;
  @Input() optionSets!: FieldConfiguratorOptionSets;
  @Input() positionX = 0;
  @Input() positionY = 0;
  @Input() checkboxGroupName = '';
  @Input() checkboxGroupValues: string[] = [];
  @Input() checkboxGroupDefaultValues: string[] = [];
  @Input() radioGroupName = '';
  @Input() radioGroupValues: string[] = [];
  @Input() radioGroupDefaultValue: string | undefined;
  @Input() previewFilter: FieldConfiguratorPreviewFilter = 'all';
  @Input() previewMeta: FieldConfiguratorPreviewMeta = {};
  @Input() fields: Array<{ role: string }> = [];
  @Input() viewerPdfSource: string | Uint8Array | Blob | null = null;
  @Input() hasPendingSource = false;
  @Input() emptyNoFileMessage = 'No file selected. Go back to Step 1 and add a PDF.';
  @Input() emptyPreparingMessage = 'Preparing PDF preview...';
  @Input() pdfLoadError = '';
  @Input() pdfLoadErrorDetail = '';
  @Input() pdfBootstrapError = '';
  @Input() showViewerInstance = true;
  @Input() viewerRenderNonce = 0;
  @Input() viewerPageBinding: number | undefined = 1;
  @Input() viewerRotation: 0 | 90 | 180 | 270 = 0;
  @Input() pdfLoading = false;
  @Input() pageCount = 0;
  @Input() retryButtonLabel = 'Retry preview';
  @Input() showStepOneButton = true;
  @Input() stepOneButtonLabel = 'Go to Step 1';
  @Output() activeActorIdChange = new EventEmitter<string>();
  @Output() paletteDragStart = new EventEmitter<{ event: DragEvent; type: FieldPaletteItem['type'] }>();
  @Output() paletteDragEnd = new EventEmitter<void>();
  @Output() paletteTileClick = new EventEmitter<FieldPaletteItem['type']>();
  @Output() walkthroughRequest = new EventEmitter<void>();
  @Output() goToStepOne = new EventEmitter<void>();
  @Output() retryPreview = new EventEmitter<void>();
  @Output() pdfLoadingStarts = new EventEmitter<void>();
  @Output() pdfLoaded = new EventEmitter<void>();
  @Output() pdfLoadingFailed = new EventEmitter<unknown>();
  @Output() pagesLoaded = new EventEmitter<unknown>();
  @Output() pageRendered = new EventEmitter<unknown>();
  @Output() pageChange = new EventEmitter<unknown>();
  @Output() rotationChange = new EventEmitter<0 | 90 | 180 | 270>();
  @Output() settingsAction = new EventEmitter<FieldConfiguratorSettingAction>();
  @Output() previewFilterChange = new EventEmitter<FieldConfiguratorPreviewFilter>();
  @Output() previewMetaChange = new EventEmitter<Partial<FieldConfiguratorPreviewMeta>>();

  actorSummaries(): Array<FieldConfiguratorActor & { fieldCount: number }> {
    return this.actors.map((actor) => ({
      ...actor,
      fieldCount: this.fields.filter((field) => field.role === actor.id).length,
    }));
  }

  visibleSummaryCount(): number {
    if (this.previewFilter === 'all') {
      return this.fields.length;
    }

    return this.fields.filter((field) => field.role === this.previewFilter).length;
  }
}
